// Fil d'Ariane de la top bar : Section › Écran (dérivés de la route) puis les
// segments publiés par la page courante (ex. le nom du compte). Une page
// appelle useBreadcrumbTrail([...]) ; le fil est vidé à son démontage.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { resolveActive, resolveChild } from './navConfig'

interface BreadcrumbContextValue {
  trail: string[]
  setTrail: (trail: string[]) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null)

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<string[]>([])
  const value = useMemo(() => ({ trail, setTrail }), [trail])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

/** Publie les segments de la page courante dans le fil ; null = rien. */
export function useBreadcrumbTrail(parts: string[] | null) {
  const ctx = useContext(BreadcrumbContext)
  const key = parts ? parts.join(' ') : ''
  useEffect(() => {
    if (!ctx) return
    ctx.setTrail(parts ?? [])
    return () => ctx.setTrail([])
  }, [ctx?.setTrail, key])
}

export function Breadcrumb() {
  const ctx = useContext(BreadcrumbContext)
  const location = useLocation()
  const navigate = useNavigate()
  const resolved = resolveActive(location.pathname)
  if (!resolved) return null
  const child = resolveChild(resolved.item, location.pathname, location.search)
  const trail = ctx?.trail ?? []
  const crumbs: { label: string; to?: string }[] = [
    { label: resolved.section.title, to: resolved.section.items[0]?.path },
    { label: resolved.item.label, to: resolved.item.path },
    ...(child && child.to !== resolved.item.path ? [{ label: child.label, to: child.to }] : []),
    ...trail.map(label => ({ label })),
  ]
  return (
    <div className="flex items-center gap-1.5 text-caption text-ink-muted min-w-0">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1
        return (
          <span key={`${i}-${c.label}`} className="flex items-center gap-1.5 min-w-0">
            {i > 0 && <span aria-hidden="true">›</span>}
            {c.to && !last ? (
              <button onClick={() => navigate(c.to!)} className="hover:text-ink transition-colors cursor-pointer whitespace-nowrap">{c.label}</button>
            ) : (
              <span className={`truncate ${last ? 'text-ink font-medium' : ''}`}>{c.label}</span>
            )}
          </span>
        )
      })}
    </div>
  )
}
